import { doc, setDoc, deleteField } from 'firebase/firestore';
import { isExamMonth } from './examMonth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from './firebase';
import { resolveZone } from './dateUtils';

// Saving a profile, shared by Profile and the first-run setup (brief 020).
// Availability is persisted as local wall-clock slots + the zone they were entered in;
// the UTC form for matching is derived on read, never stored.
export function profileDoc(profile, uid) {
  const data = { ...profile, name: (profile.name || '').trim(), availabilityTz: resolveZone(profile.timezone), uid, updatedAt: new Date().toISOString() };
  delete data.availability;
  // Contact info stays off the public profile; prefs and storage are owned by other screens.
  delete data.email; delete data.phone;
  delete data.notificationPreferences; delete data.storageUsedBytes; delete data.reputation;
  // Set only in the console (brief 034); the rules refuse it from the app.
  delete data.demo;
  // Planned exam month (P-2): "YYYY-MM", or removed when not decided.
  data.examMonth = isExamMonth(profile.examMonth) ? profile.examMonth : deleteField();
  return data;
}

// Subjects ticked in both lists (brief 025). New picks can't do this; older profiles may.
export const doubledSubjects = p => (p.strengths || []).filter(s => (p.wantsToLearn || []).includes(s));

export const saveProfile = (profile, uid) => setDoc(doc(db, 'users', uid), profileDoc(profile, uid), { merge: true });

// 1080 px on the long side stays sharp large on a 3x iPhone (brief 029).
const resizeImage = (file, maxDim = 1080) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read that image.'));
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error('That file is not a readable image.'));
    img.onload = () => {
      let { width, height } = img;
      if (width > height && width > maxDim) { height = Math.round(height * maxDim / width); width = maxDim; }
      else if (height > maxDim) { width = Math.round(width * maxDim / height); height = maxDim; }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(b => b ? resolve(b) : reject(new Error('Could not process that image.')), 'image/jpeg', 0.85);
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

// Resizes to 400 px, uploads as the user's avatar and saves its URL on the profile.
export async function uploadProfilePhoto(uid, file) {
  if (!file.type.startsWith('image/')) throw new Error('Please choose an image file.');
  const blob = await resizeImage(file);
  const storage = getStorage();
  const path = `profilePictures/${uid}/avatar.jpg`;
  await uploadBytes(ref(storage, path), blob, { contentType: 'image/jpeg' });
  const url = (await getDownloadURL(ref(storage, path))) + '&t=' + Date.now();
  await setDoc(doc(db, 'users', uid), { profilePicture: url }, { merge: true });
  return url;
}
